import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  UncontrolledTooltip,
  UncontrolledAccordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
} from "reactstrap";
import { CustomizationSpinnerContainer, Notification } from "../common/style";
import { getOrderDetails, getSignedOrderDetailsDownloadURL } from "./service";
import { downloadShipmentInvoice } from "../dashboards/ops/service";
import { Header } from "../common";
import { formatDateWithSuffix } from "../common/DateHelpers";

const OrderDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState({});

  // Tab states
  const [activeMainTab, setActiveMainTab] = useState("delivery");
  const [activeDeliveryTab, setActiveDeliveryTab] = useState("cabinets");
  const [selectedShipment, setSelectedShipment] = useState(null);

  // Search states
  const [cabinetSearch, setCabinetSearch] = useState("");
  const [packageSearch, setPackageSearch] = useState("");


  useEffect(() => {
    if (projectId) {
      loadOrderDetails();
    }
  }, [projectId]);

  const loadOrderDetails = () => {
    setIsLoading(true);
    getOrderDetails(projectId)
      .then((details) => {
        setOrderDetails(details);
        // Don't auto-select first shipment - user must click on a shipment from the listing
        setSelectedShipment(null);
      })
      .catch((error) => {
        setIsNotificationVisible(true);
        setNotificationMessage({
          type: "error",
          text: error.error || error.message || "Failed to load order details",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const downloadOrderDetails = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setIsLoading(true);

    getSignedOrderDetailsDownloadURL({ projectId })
      .then((response) => {
        const downloadUrl = response.url || response;
        window.open(downloadUrl, "_blank", "noreferrer");
      })
      .catch((error) => {
        setIsNotificationVisible(true);
        setNotificationMessage({
          type: "error",
          text: error?.error || "Error downloading order details",
        });
      })
      .finally(() => setIsLoading(false));
  };

  const handleDownloadInvoice = async (e, shipment) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    
    if (!orderDetails?.order?.id) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "error",
        text: "Order ID not found",
      });
      return;
    }

    try {
      setIsLoading(true);
      const response = await downloadShipmentInvoice({
        orderId: orderDetails.order.id,
        shipmentId: shipment.id,
      });

      const blob = response.data;
      const contentDisposition = response.headers['content-disposition'];

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      let filename = 'invoice.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setIsNotificationVisible(true);
      setNotificationMessage({ type: "success", text: "Invoice downloaded successfully" });
    } catch (error) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "danger",
        text: "Failed to download invoice: " + (error.message || error.error || error || "Unknown error")
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "-";
    return `₹${Number(value).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    try {
      const date = new Date(dateString);
      return formatDateWithSuffix(date) || date.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatStatus = (status) => {
    if (!status) return "";
    return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");
  };

  const getShipmentStatus = (shipment) => {
    if (shipment.status === "delivered") return "Delivered";
    if (shipment.status === "dispatch") return "Dispatched";
    if (shipment.status === "ready_to_dispatch") return "Ready";
    if (shipment.status === "confirmed") return "Pending";
    return shipment.status ? formatStatus(shipment.status) : "Pending";
  };

  const isShipmentDispatched = (shipment) => {
    return shipment.status === "dispatch" || shipment.status === "delivered";
  };

  const hasPackages = (shipment) => {
    return shipment?.packages && shipment.packages.length > 0;
  };

  // Helper to programmatically open accordion (for search)
  const openAccordion = (id) => {
    // Find the accordion header button and click it
    const element = document.querySelector(`[aria-controls="${id}"]`);
    if (element) {
      element.click();
    }
  };

  // Search functionality for cabinets
  const matchesCabinetSearch = (cabinetLabel) => {
    if (!cabinetSearch.trim()) return true;
    return String(cabinetLabel)?.toLowerCase() === cabinetSearch.toLowerCase();
  };

  // Search functionality for packages
  const matchesPackageSearch = (packageData) => {
    if (!packageSearch.trim()) return true;
    const search = packageSearch.trim();
    // Check if package contains panels with matching assembly_label (number after #)
    if (packageData?.panels) {
      return packageData.panels.some(panel => {
        const labelNumber = extractNumberFromLabel(panel?.assembly_label);
        return labelNumber === search;
      });
    }
    return false;
  };

  // Extract number after # from panel label (e.g., "#1 C1 / 1" -> "1")
  const extractNumberFromLabel = (label) => {
    if (!label) return null;
    const match = String(label).match(/^#(\d+)/);
    return match ? match[1] : null;
  };

  // Filter panels by cabinet search
  const filterPanelsByCabinet = (panels) => {
    if (!cabinetSearch.trim()) return panels || [];
    const search = cabinetSearch.trim();
    return panels?.filter(panel => {
      // Check assembly_label for number after #
      const labelNumber = extractNumberFromLabel(panel?.assembly_label);
      if (labelNumber === search) return true;

      // Also check cabinet_label and cabinet_id for backward compatibility
      const cabinetLabelNumber = extractNumberFromLabel(panel?.cabinet_label);
      if (cabinetLabelNumber === search) return true;

      return String(panel?.cabinet_label)?.toLowerCase() === search.toLowerCase() ||
             String(panel?.cabinet_id)?.toLowerCase() === search.toLowerCase();
    }) || [];
  };

  // Get panel count for a cabinet
  const getCabinetPanelCount = (cabinet) => {
    if (!cabinet?.panels) return 0;
    if (cabinetSearch.trim()) {
      return filterPanelsByCabinet(cabinet.panels).length;
    }
    return cabinet.panels.length;
  };

  // Get package panel count (filtered by search)
  const getPackagePanelCount = (packageData) => {
    if (!packageData?.panels) return 0;
    if (packageSearch.trim()) {
      return filterPanelsByCabinet(packageData.panels).length;
    }
    return packageData.panels.length;
  };

  // Find cabinet by label and open it
  const findAndOpenCabinet = (cabinetLabel) => {
    if (!cabinetLabel || !selectedShipment?.cabinets) return;

    // Switch to delivery tab and cabinets sub-tab
    setActiveMainTab("delivery");
    setActiveDeliveryTab("cabinets");

    for (const room of selectedShipment.cabinets || []) {
      for (const section of room.sections || []) {
        for (const cabinet of section.order_items || []) {
          if (String(cabinet.assembly_label)?.toLowerCase() === String(cabinetLabel).toLowerCase()) {
            setCabinetSearch(String(cabinet.assembly_label));
            // Open the accordion for this cabinet's room, section, and cabinet
            setTimeout(() => {
              const roomId = `room_${room.id}`;
              openAccordion(roomId);
              setTimeout(() => {
                const sectionId = `section_${room.id}_${section.id}`;
                openAccordion(sectionId);
                setTimeout(() => {
                  const cabinetId = `cabinet_${cabinet.id || cabinet.assembly_label}`;
                  openAccordion(cabinetId);
                }, 100);
              }, 100);
            }, 100);
            return;
          }
        }
      }
    }
  };

  // Handle panel click - switch to package tab and open package
  const handlePanelClick = (panel) => {
    if (!panel?.package_id) return;
    setActiveMainTab("delivery");
    setActiveDeliveryTab("packages");
    setTimeout(() => {
      openAccordion(`package_${panel.package_id}`);
    }, 100);
  };

  // Handle package panel click - switch to cabinets tab and open cabinet
  const handlePackagePanelClick = (panel) => {
    if (!panel?.assembly_label) return;
    // Extract number from assembly_label (e.g., "#1 D1 / 1" -> "1")
    const labelNumber = extractNumberFromLabel(panel.assembly_label);
    if (labelNumber) {
      findAndOpenCabinet(labelNumber);
    }
  };

  // Render Panel Card
  const renderPanelCard = (panel, shipmentId, disabled = false) => {
    const isNotInShipment = shipmentId && panel?.shipment_id && panel.shipment_id !== shipmentId;
    const isInShipmentFalse = panel?.in_shipment === false;
    const isDisabled = disabled || isNotInShipment || isInShipmentFalse;

    // Check if panel shipment is different from current shipment
    const panelShipment = panel?.shipment || null;
    const currentShipmentName = selectedShipment?.shipment_name || selectedShipment?.name || (selectedShipment ? `Shipment #${selectedShipment.number || selectedShipment.id}` : null);
    const showShipmentPill = panelShipment && panelShipment !== currentShipmentName;

    return (
      <div
        className={`card h-100 ${isDisabled ? "opacity-60" : ""}`}
        style={{
          cursor: isDisabled ? "not-allowed" : "pointer",
          backgroundColor: isInShipmentFalse ? "#f8f9fa" : "#ffffff",
          position: "relative",
          border: isDisabled ? "1px solid #e0e0e0" : "1px solid #d0d0d0",
          borderRadius: "8px",
          boxShadow: isDisabled ? "none" : "0 2px 4px rgba(0,0,0,0.1)",
          transition: "all 0.2s ease-in-out",
          overflow: "hidden"
        }}
        onMouseEnter={(e) => {
          if (!isDisabled) {
            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.borderColor = "#2C92E6";
          }
        }}
        onMouseLeave={(e) => {
          if (!isDisabled) {
            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.borderColor = "#d0d0d0";
          }
        }}
        onClick={() => {
          if (!isDisabled) {
            if (panel?.package_id) {
              handlePanelClick(panel);
            }
          }
        }}
      >
        {showShipmentPill && (
          <span
            className="badge"
            style={{
              position: "absolute",
              top: "0.25rem",
              right: "0.25rem",
              fontSize: "0.6rem",
              zIndex: 10,
              backgroundColor: "#6c757d",
              color: "#ffffff",
              padding: "0.15rem 0.35rem",
              borderRadius: "10px",
              fontWeight: "500"
            }}
          >
            In {panelShipment}
          </span>
        )}
        <div className="card-body d-flex flex-column justify-content-center align-items-center text-center" style={{ minHeight: "90px", padding: "0.75rem 0.5rem", gap: "0.25rem" }}>
          <div
            className="fw-bold"
            style={{
              fontSize: '0.75rem',
              color: isDisabled ? "#999" : "#666",
              letterSpacing: "0.2px",
              marginTop: "0.5rem"
            }}
          >
            {panel?.assembly_label|| "N/A"}
          </div>
          <div
            className="text-secondary"
            style={{
              fontSize: '0.7rem',
              color: isDisabled ? "#bbb" : "#666",
              lineHeight: "1.2",
              minHeight: "1.8rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "0"
            }}
          >
            {panel?.name || panel?.panel_name || ""}
          </div>
          {!isDisabled && panel?.package_id && (
            <div
              className="text-muted"
              style={{
                fontSize: '0.65rem',
                color: "#999",
                paddingTop: "0.5rem",
                borderTop: "1px solid #f0f0f0",
                width: "100%"
              }}
            >
              Package #{panel.package_id}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Cabinets View
  const renderCabinetsView = () => {
    if (!selectedShipment || !selectedShipment.cabinets || selectedShipment.cabinets.length === 0) {
      return <div className="text-center p-4">No cabinets available</div>;
    }

    const shipmentId = selectedShipment?.id;
    const rooms = selectedShipment.cabinets || []; // cabinets array contains rooms
    const totalOrderPanels = orderDetails?.order?.total_order_panels || 0;

    return (
      <div style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
        <style>{`
          .accordion-item {
            border: none !important;
            background: transparent !important;
            margin-bottom: 0 !important;
            width: 100% !important;
          }
          .accordion-button {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
            padding: 0.1rem 0 0.1rem 0 !important;
            width: 100% !important;
            display: flex !important;
            flex-direction: row !important;
            justify-content: flex-start !important;
          }
          .accordion-button::after {
            display: none !important;
          }
          .accordion-button:not(.collapsed)::after {
            display: none !important;
          }
          .accordion-chevron {
            transition: transform 0.2s ease;
            margin-right: 0.5rem;
            font-size: 0.7rem;
          }
          .accordion-button:not(.collapsed) .accordion-chevron {
            transform: rotate(90deg);
          }
          .accordion-button:not(.collapsed) {
            background: transparent !important;
            box-shadow: none !important;
          }
          .accordion-body {
            background: transparent !important;
            border: none !important;
            padding: 0.05rem 0 0.05rem 0.5rem !important;
            width: 100% !important;
          }
          @media (min-width: 768px) {
            .panel-card-wrapper {
              flex: 0 0 20% !important;
              max-width: 20% !important;
            }
            .package-panel-card-wrapper {
              flex: 0 0 33.333% !important;
              max-width: 33.333% !important;
            }
          }
          @media (max-width: 767px) {
            .panel-card-wrapper {
              flex: 0 0 33.333% !important;
              max-width: 33.333% !important;
            }
            .package-panel-card-wrapper {
              flex: 0 0 33.333% !important;
              max-width: 33.333% !important;
            }
          }
        `}</style>
        <UncontrolledAccordion stayOpen className="ms-1">
          {rooms.map((room) => {
            const sections = room.sections || [];
            const roomId = `room_${room.id}`;
            const roomPanelsCount = room.panels_count || 0;
            const roomTotalPanels = room.total_panels_count || totalOrderPanels;

          // Check if room-level order_items match search
          const hasMatchingRoomOrderItem = room.order_items && room.order_items.length > 0 && cabinetSearch.trim() ?
            room.order_items.some(cabinet => {
              const search = cabinetSearch.trim();
              if (String(cabinet.assembly_label)?.toLowerCase() === search.toLowerCase()) return true;
              const panels = cabinet.panels || [];
              return panels.some(panel => {
                const labelNumber = extractNumberFromLabel(panel?.assembly_label);
                return labelNumber === search;
              });
            }) : false;

          // Filter sections based on search - show only if has matching cabinet
          const visibleSections = sections.filter(section => {
            if (!cabinetSearch.trim()) return true;
            const search = cabinetSearch.trim();
            const hasMatchingCabinet = (section.order_items || []).some(cabinet => {
              // Check if cabinet assembly_label matches
              if (String(cabinet.assembly_label)?.toLowerCase() === search.toLowerCase()) return true;

              // Check if any panel in this cabinet has matching number after #
              const panels = cabinet.panels || [];
              return panels.some(panel => {
                const labelNumber = extractNumberFromLabel(panel?.assembly_label);
                return labelNumber === search;
              });
            });
            return hasMatchingCabinet;
          });

          return (
            <AccordionItem key={room.id} style={{ border: 'none', background: 'transparent', marginBottom: '0', width: '100%' }}>
              <AccordionHeader targetId={roomId} style={{ background: 'transparent', border: 'none', padding: '0.1rem 0', width: '100%' }}>
                <div className="w-100 d-flex justify-content-between align-items-center" style={{ width: '100%' }}>
                  <span>
                    <i className="fa-solid fa-chevron-right accordion-chevron"></i>
                    {room.name}                                   {roomTotalPanels > 0 && (
                      <span className="ms-2 text-muted small">
                        {(() => {
                          const hardwaresCount = room.hardwares_count || 0;
                          const panelsText = `${roomPanelsCount}/${roomTotalPanels} panel${roomPanelsCount !== 1 ? 's' : ''}`;
                          const hardwaresText = hardwaresCount > 0
                            ? `${hardwaresCount} hardware${hardwaresCount !== 1 ? 's' : ''}`
                            : "-";
                          return `${panelsText} | ${hardwaresText}`;
                        })()}
                      </span>
                    )}
                  </span>
                </div>
              </AccordionHeader>
              <AccordionBody accordionId={roomId} style={{ background: 'transparent', border: 'none', padding: '0.05rem 0 0.05rem 0.5rem' }}>
                {/* Show room-level order_items first */}
                {room.order_items && room.order_items.length > 0 && (
                  <UncontrolledAccordion stayOpen className="ms-1">
                    {room.order_items
                      .filter(cabinet => {
                        if (!cabinetSearch.trim()) return true;
                        const search = cabinetSearch.trim();
                        // Check if cabinet assembly_label matches
                        if (String(cabinet.assembly_label)?.toLowerCase() === search.toLowerCase()) return true;
                        // Check if any panel in this cabinet has matching number after #
                        const panels = cabinet.panels || [];
                        return panels.some(panel => {
                          const labelNumber = extractNumberFromLabel(panel?.assembly_label);
                          return labelNumber === search;
                        });
                      })
                      .map((cabinet) => {
                        const panels = cabinet.panels || [];
                        const filteredPanels = cabinetSearch.trim()
                          ? filterPanelsByCabinet(panels)
                          : panels;
                        const cabinetPanelsCount = cabinet.panels_count || 0;
                        const cabinetTotalPanels = cabinet.total_panels_count || totalOrderPanels;
                        const deliveredCount = panels.filter(p => p.delivered).length;
                        const totalCount = panels.length;

                        return (
                          <AccordionItem key={cabinet.id || cabinet.assembly_label} style={{ border: 'none', background: 'transparent', marginBottom: '0', width: '100%' }}>
                            <AccordionHeader targetId={`cabinet_${cabinet.id || cabinet.assembly_label}`} style={{ background: 'transparent', border: 'none', padding: '0.1rem 0', width: '100%' }}>
                              <div className="w-100 d-flex justify-content-between align-items-center" style={{ width: '100%' }}>
                                <span>
                                  <i className="fa-solid fa-chevron-right accordion-chevron"></i>
                                  {cabinet.name || cabinet.assembly_label || `Cabinet #${cabinet.id}`}
                                  {cabinetTotalPanels > 0 && (
                                    <span className="ms-2 text-muted small">
                                      {(() => {
                                        const hardwaresCount = cabinet.hardwares_count || 0;
                                        const panelsText = `${cabinetPanelsCount}/${cabinetTotalPanels} panel${cabinetPanelsCount !== 1 ? 's' : ''}`;
                                        const hardwaresText = hardwaresCount > 0
                                          ? `${hardwaresCount} hardware${hardwaresCount !== 1 ? 's' : ''}`
                                          : "-";
                                        return `${panelsText} | ${hardwaresText}`;
                                      })()}
                                      {deliveredCount === totalCount && totalCount > 0 && (
                                        <span className="ms-1 text-success">
                                          &lt;delivered&gt;
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </span>
                              </div>
                            </AccordionHeader>
                            <AccordionBody accordionId={`cabinet_${cabinet.id || cabinet.assembly_label}`} style={{ background: 'transparent', border: 'none', padding: '0.05rem 0 0.05rem 0.5rem' }}>
                              {filteredPanels.length > 0 ? (
                                <div className="row">
                                  {filteredPanels.map((panel) => (
                                    <div key={panel?.id || panel?.panel_id} className="col-12 panel-card-wrapper mb-2">
                                      {renderPanelCard(panel, shipmentId)}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-muted small">No panels</div>
                              )}
                            </AccordionBody>
                          </AccordionItem>
                        );
                      })}
                  </UncontrolledAccordion>
                )}

                {/* Then show sections */}
                {sections.length === 0 && (!room.order_items || room.order_items.length === 0) ? (
                  <div className="text-muted ms-3">No sections or cabinets available</div>
                ) : visibleSections.length === 0 && cabinetSearch.trim() && !hasMatchingRoomOrderItem ? (
                  <div className="text-muted ms-3">No matching cabinets found</div>
                ) : sections.length > 0 ? (
                  <UncontrolledAccordion stayOpen className="ms-1">
                    {visibleSections.map((section) => {
                      const sectionId = `section_${room.id}_${section.id}`;
                      const cabinets = section.order_items || []; // cabinets are in order_items

                      // Filter cabinets - exact match if search exists
                      const visibleCabinets = cabinets.filter(cabinet => {
                        if (!cabinetSearch.trim()) return true;
                        const search = cabinetSearch.trim();

                        // Check if cabinet assembly_label matches
                        if (String(cabinet.assembly_label)?.toLowerCase() === search.toLowerCase()) return true;

                        // Check if any panel in this cabinet has matching number after #
                        const panels = cabinet.panels || [];
                        return panels.some(panel => {
                          const labelNumber = extractNumberFromLabel(panel?.assembly_label);
                          return labelNumber === search;
                        });
                      });

                      if (visibleCabinets.length === 0) return null;

                      const sectionPanelsCount = section.panels_count || 0;
                      const sectionTotalPanels = section.total_panels_count || totalOrderPanels;

                      return (
                        <AccordionItem key={section.id} style={{ border: 'none', background: 'transparent', marginBottom: '0', width: '100%' }}>
                          <AccordionHeader targetId={sectionId} style={{ background: 'transparent', border: 'none', padding: '0.1rem 0', width: '100%' }}>
                            <div className="w-100 d-flex justify-content-between align-items-center" style={{ width: '100%' }}>
                              <span>
                                <i className="fa-solid fa-chevron-right accordion-chevron"></i>
                                {section.name || "Section"}                                   {sectionTotalPanels > 0 && (
                                  <span className="ms-2 text-muted small">
                                    {(() => {
                                      const hardwaresCount = section.hardwares_count || 0;
                                      const panelsText = `${sectionPanelsCount}/${sectionTotalPanels} panel${sectionPanelsCount !== 1 ? 's' : ''}`;
                                      const hardwaresText = hardwaresCount > 0
                                        ? `${hardwaresCount} hardware${hardwaresCount !== 1 ? 's' : ''}`
                                        : "-";
                                      return `${panelsText} | ${hardwaresText}`;
                                    })()}
                                  </span>
                                )}
                              </span>
                            </div>
                          </AccordionHeader>
                          <AccordionBody accordionId={sectionId} style={{ background: 'transparent', border: 'none', padding: '0.05rem 0 0.05rem 0.5rem' }}>
                            {visibleCabinets.length > 0 ? (
                              <UncontrolledAccordion stayOpen>
                                {visibleCabinets.map((cabinet) => {
                                  const panels = cabinet.panels || [];
                                  const filteredPanels = cabinetSearch.trim()
                                    ? filterPanelsByCabinet(panels)
                                    : panels;
                                  const cabinetPanelsCount = cabinet.panels_count || 0;
                                  const cabinetTotalPanels = cabinet.total_panels_count || totalOrderPanels;
                                  const deliveredCount = panels.filter(p => p.delivered).length;
                                  const totalCount = panels.length;

                                  return (
                                    <AccordionItem key={cabinet.id || cabinet.assembly_label} style={{ border: 'none', background: 'transparent', marginBottom: '0', width: '100%' }}>
                                      <AccordionHeader targetId={`cabinet_${cabinet.id || cabinet.assembly_label}`} style={{ background: 'transparent', border: 'none', padding: '0.1rem 0', width: '100%' }}>
                                        <div className="w-100 d-flex justify-content-between align-items-center" style={{ width: '100%' }}>
                                          <span>
                                            <i className="fa-solid fa-chevron-right accordion-chevron"></i>
                                            {cabinet.name || cabinet.assembly_label || `Cabinet #${cabinet.id}`}
                                            {cabinetTotalPanels > 0 && (
                                              <span className="ms-2 text-muted small">
                                                {cabinetPanelsCount}/{cabinetTotalPanels} panels
                                                {deliveredCount === totalCount && totalCount > 0 && (
                                                  <span className="ms-1 text-success">
                                                    &lt;delivered&gt;
                                                  </span>
                                                )}
                                              </span>
                                            )}
                                          </span>
                                        </div>
                                      </AccordionHeader>
                                      <AccordionBody accordionId={`cabinet_${cabinet.id || cabinet.assembly_label}`} style={{ background: 'transparent', border: 'none', padding: '0.05rem 0 0.05rem 0.5rem' }}>
                                        {filteredPanels.length > 0 ? (
                                          <div className="row">
                                            {filteredPanels.map((panel) => (
                                              <div key={panel?.id || panel?.panel_id} className="col-12 panel-card-wrapper mb-2">
                                                {renderPanelCard(panel, shipmentId)}
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="text-muted small">No panels</div>
                                        )}
                                      </AccordionBody>
                                    </AccordionItem>
                                  );
                                })}
                              </UncontrolledAccordion>
                            ) : (
                              <div className="text-muted small">No cabinets</div>
                            )}
                          </AccordionBody>
                        </AccordionItem>
                      );
                    })}
                  </UncontrolledAccordion>
                ) : null}
              </AccordionBody>
            </AccordionItem>
          );
        })}
        </UncontrolledAccordion>
      </div>
    );
  };

  // Render Packages View
  const renderPackagesView = () => {
    if (!selectedShipment) {
      return <div className="text-center p-4">No packages available</div>;
    }

    // Check if shipment is dispatched or delivered
    if (!isShipmentDispatched(selectedShipment)) {
      return (
        <div className="text-center p-5">
          <p className="text-muted mb-0">
            You will see the package details here once this shipment is delivered
          </p>
        </div>
      );
    }

    if (!selectedShipment.packages || selectedShipment.packages.length === 0) {
      return <div className="text-center p-4">No packages available for this shipment</div>;
    }

    // Filter packages based on search
    const packages = (selectedShipment.packages || []).filter(pkg => {
      if (!packageSearch.trim()) return true;
      const search = packageSearch.trim();
      // Show package if it has panels matching the search (number after #)
      return (pkg.panels || []).some(panel => {
        const labelNumber = extractNumberFromLabel(panel?.assembly_label);
        if (labelNumber === search) return true;

        // Also check cabinet_label and cabinet_id for backward compatibility
        const cabinetLabelNumber = extractNumberFromLabel(panel?.cabinet_label);
        if (cabinetLabelNumber === search) return true;

        return String(panel?.cabinet_label)?.toLowerCase() === search.toLowerCase() ||
               String(panel?.cabinet_id)?.toLowerCase() === search.toLowerCase();
      });
    });

    return (
      <div style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
        <style>{`
          .accordion-button::after {
            display: none !important;
          }
          .accordion-button:not(.collapsed)::after {
            display: none !important;
          }
          .accordion-chevron {
            transition: transform 0.2s ease;
            margin-right: 0.5rem;
            font-size: 0.7rem;
          }
          .accordion-button:not(.collapsed) .accordion-chevron {
            transform: rotate(90deg);
          }
          @media (min-width: 768px) {
            .package-panel-card-wrapper {
              flex: 0 0 33.333% !important;
              max-width: 33.333% !important;
            }
          }
          @media (max-width: 767px) {
            .package-panel-card-wrapper {
              flex: 0 0 33.333% !important;
              max-width: 33.333% !important;
            }
          }
        `}</style>
        <UncontrolledAccordion stayOpen className="ms-1">
          {packages.map((pkg) => {
            const packageId = `package_${pkg.id || pkg.package_number}`;
            const panels = pkg.panels || [];

            // Filter panels by cabinet search - show only matching cabinet panels
            const filteredPanels = packageSearch.trim()
              ? panels.filter(panel => {
                  const search = packageSearch.trim();
                  const labelNumber = extractNumberFromLabel(panel?.assembly_label);
                  if (labelNumber === search) return true;

                  // Also check cabinet_label and cabinet_id for backward compatibility
                  const cabinetLabelNumber = extractNumberFromLabel(panel?.cabinet_label);
                  if (cabinetLabelNumber === search) return true;

                  return String(panel?.cabinet_label)?.toLowerCase() === search.toLowerCase() ||
                         String(panel?.cabinet_id)?.toLowerCase() === search.toLowerCase();
                })
              : panels;

            // Panel count should only show filtered panels if search is active
            const panelCount = packageSearch.trim() ? filteredPanels.length : panels.length;

            return (
              <AccordionItem key={pkg.id || pkg.package_number}>
                <AccordionHeader targetId={packageId} style={{ background: 'transparent', border: 'none', padding: '0.1rem 0', width: '100%' }}>
                  <div className="w-100 d-flex justify-content-between align-items-center" style={{ width: '100%' }}>
                    <span>
                      <i className="fa-solid fa-chevron-right accordion-chevron"></i>
                      Package #{pkg.package_number || pkg.id}
                      {panelCount > 0 && (
                        <span className="ms-2 text-muted small">
                          {(() => {
                            const hardwaresCount = pkg.hardwares_count || 0;
                            const panelsText = `${panelCount} panel${panelCount !== 1 ? 's' : ''}`;
                            const hardwaresText = hardwaresCount > 0
                              ? `${hardwaresCount} hardware${hardwaresCount !== 1 ? 's' : ''}`
                              : "-";
                            return `${panelsText} | ${hardwaresText}`;
                          })()}
                        </span>
                      )}
                    </span>
                  </div>
                </AccordionHeader>
                <AccordionBody accordionId={packageId}>
                  {filteredPanels.length > 0 ? (
                    <div className="row">
                      {filteredPanels.map((panel) => (
                        <div key={panel?.id || panel?.panel_id} className="col-12 package-panel-card-wrapper mb-2">
                          {renderPanelCard(panel, selectedShipment?.id, false, handlePackagePanelClick)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted small ms-3">No panels</div>
                  )}
                </AccordionBody>
              </AccordionItem>
            );
          })}
        </UncontrolledAccordion>
      </div>
    );
  };

  // Render Delivery Tab
  const renderDeliveryTab = () => {
    if (!orderDetails) {
      return <div className="text-center p-4">Loading...</div>;
    }

    const shipments = orderDetails.shipments || [];

    // If no shipment selected, show shipment listing
    if (!selectedShipment) {
      return (
        <div style={{ paddingLeft: 0, paddingRight: 0 }}>
          {shipments.length === 0 ? (
            <div className="text-center p-4">No shipments available</div>
          ) : (
            shipments.map((shipment) => {
              const shipmentPanels = shipment.total_panels || shipment.parts_count || shipment.panels_count || 0;
              const orderTotalPanels = orderDetails.order?.total_order_panels || 0;
              const status = getShipmentStatus(shipment);

              return (
                <div
                  key={shipment.id}
                  className="card mb-3"
                  style={{ cursor: "pointer", marginLeft: '1rem', marginRight: '1rem', borderRadius: 0 }}
                  onClick={() => {
                    setSelectedShipment(shipment);
                    setCabinetSearch("");
                    setPackageSearch("");
                  }}
                >
                  <div className="card-body" style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="mb-1">
                          {shipment.shipment_name || shipment.name || `Shipment #${shipment.number || shipment.id}`}
                          {shipment.description && (
                            <span className="text-muted" style={{ fontSize: '0.9rem', fontWeight: 'normal' }}>
                              {" "}({shipment.description})
                            </span>
                          )}
                        </h6>
                        <div className="text-muted small">
                          {(() => {
                            const hardwaresCount = shipment.hardwares_count || 0;
                            const panelsText = `${shipmentPanels}/${orderTotalPanels} panel${shipmentPanels !== 1 ? 's' : ''}`;
                            const hardwaresText = hardwaresCount > 0
                              ? `${hardwaresCount} hardware${hardwaresCount !== 1 ? 's' : ''}`
                              : "-";
                            return `${panelsText} | ${hardwaresText}`;
                          })()}
                        </div>
                        {shipment.invoice_value !== null && shipment.invoice_value !== undefined && (
                          <div className="mt-1 d-flex align-items-center gap-2" style={{ fontSize: '0.875rem' }}>
                            <span>{formatCurrency(Math.round(shipment.invoice_value))}</span>
                            {(shipment.status === "dispatched" || shipment.status === "delivered") && (
                              <i
                                className="fa fa-download pointer text-primary"
                                style={{ fontSize: "0.9rem", cursor: "pointer" }}
                                title="Download Invoice"
                                onClick={(e) => handleDownloadInvoice(e, shipment)}
                              />
                            )}
                          </div>
                        )}
                      </div>
                      <div className="d-flex flex-column align-items-end text-end">
                        <small className="text-muted mb-1">{formatDate(shipment.planned_date)}</small>
                        <div className="d-flex align-items-end text-end gap-2">
                          {shipment.otp && (
                            <span
                              className="badge rounded-pill"
                              style={{
                                backgroundColor: "#ffc107",
                                color: "#000",
                                fontWeight: 600,
                              }}
                            >
                              OTP: {shipment.otp}
                            </span>
                          )}
                          <span
                            className={`badge ${
                              status === "Delivered" ? "bg-success" :
                              status === "Dispatched" ? "bg-primary" :
                              status === "Ready" ? "bg-info" :
                              "bg-secondary"
                            }`}
                          >
                            {status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      );
    }

    // If shipment is selected, show Cabinets/Packages sub-tabs
    return (
      <div style={{ paddingLeft: 0, paddingRight: 0, paddingTop: '1.5rem' }}>
        <style>{`
          .delivery-tab-btn.btn-outline-primary {
            border-color: #2C92E6 !important;
            color: #2C92E6 !important;
          }
          .delivery-tab-btn.btn-outline-primary:hover {
            background-color: #2C92E6 !important;
            border-color: #2C92E6 !important;
            color: #ffffff !important;
          }
          .delivery-tab-btn.btn-primary {
            background-color: #2C92E6 !important;
            border-color: #2C92E6 !important;
          }
          .delivery-tab-btn.btn-primary:hover {
            background-color: #1a7bc4 !important;
            border-color: #1a7bc4 !important;
          }
        `}</style>
        <div className="d-flex align-items-center mb-3" style={{ paddingLeft: '1rem' }}>
          <i
            className="fa-solid fa-arrow-left"
            style={{ cursor: "pointer", fontSize: '1.2rem', marginRight: '0.75rem' }}
            onClick={() => {
              setSelectedShipment(null);
              setCabinetSearch("");
              setPackageSearch("");
            }}
          ></i>
          <div>
            <h5 className="m-0" style={{ fontSize: 'clamp(0.875rem, 3vw, 1.25rem)' }}>
              {selectedShipment.shipment_name || selectedShipment.name || `Shipment #${selectedShipment.number || selectedShipment.id}`}
            </h5>
            <small className="text-muted" style={{ fontSize: '0.75rem' }}>{formatDate(selectedShipment.planned_date)}</small>
          </div>
        </div>

        {/* Search and Sub-tabs for Cabinets and Packages */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap" style={{ paddingLeft: '1rem', paddingRight: '1rem', gap: '1rem' }}>
          <div className="d-flex gap-2 flex-wrap">
            <button
              className={`btn delivery-tab-btn ${activeDeliveryTab === "cabinets" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setActiveDeliveryTab("cabinets")}
              style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
            >
              Cabinets
            </button>
            <button
              className={`btn delivery-tab-btn ${activeDeliveryTab === "packages" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                if (!hasPackages(selectedShipment)) {
                  setIsNotificationVisible(true);
                  setNotificationMessage({
                    type: "warning",
                    text: "No packages available for this shipment."
                  });
                  return;
                }
                setActiveDeliveryTab("packages");
              }}
              disabled={!hasPackages(selectedShipment)}
              style={{
                opacity: hasPackages(selectedShipment) ? 1 : 0.5,
                fontSize: '0.875rem',
                padding: '0.375rem 0.75rem'
              }}
            >
              Packages
            </button>
          </div>
          <div style={{ flex: '1', minWidth: '200px', maxWidth: '75%', display: 'flex', justifyContent: 'flex-end' }}>
            {activeDeliveryTab === "cabinets" ? (
              <Input
                type="text"
                placeholder="Search a cabinet number"
                value={cabinetSearch}
                onChange={(e) => setCabinetSearch(e.target.value)}
                className="form-control"
                style={{ fontSize: '0.875rem' }}
              />
            ) : (
              <Input
                type="text"
                placeholder="Search a cabinet number"
                value={packageSearch}
                onChange={(e) => setPackageSearch(e.target.value)}
                className="form-control"
                style={{ fontSize: '0.875rem' }}
              />
            )}
          </div>
        </div>

        {/* Sub-tab Content */}
        {activeDeliveryTab === "cabinets" ? (
          renderCabinetsView()
        ) : (
          renderPackagesView()
        )}
      </div>
    );
  };

  // Render Payments Tab
  const renderPaymentsTab = () => {
    return (
      <div style={{ paddingLeft: '1rem', paddingRight: 0 }}>
        <h5>Payments</h5>
        <p className="text-muted">Payment information will be displayed here.</p>
        {/* TODO: Implement payments view */}
      </div>
    );
  };

  if (isLoading) {
    return (
      <>

        <CustomizationSpinnerContainer top="200px">
          <Spinner>Loading...</Spinner>
        </CustomizationSpinnerContainer>
      </>
    );
  }

  if (!orderDetails) {
    return (
      <>

        <div className="container-fluid px-0 py-4" style={{ width: '100%', margin: 0 }}>
          <div className="alert alert-danger">No order details found</div>
        </div>
      </>
    );
  }

  return (
    <>
      {notificationMessage.text && (
        <Notification
          color={notificationMessage.type}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notificationMessage.text}
        </Notification>
      )}

      <div className="container-fluid px-0 py-0" style={{ width: '100%', margin: 0, paddingTop: 0 }}>
        {/* Header - Hide when shipment is selected */}
        {!selectedShipment && (
          <>
            <div className="card mb-4" style={{ marginLeft: 0, marginRight: 0, marginTop: 0, borderRadius: 0, borderTop: 'none' }}>
              <div className="card-body" style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem' }}>
                <div className="row align-items-start">
                  <div className="col-6">
                    <h4 className="mb-1 d-flex align-items-center" style={{ fontSize: '1.1rem', marginBottom: '0.25rem' }}>
                      <Link
                        to="/projects"
                        className="text-decoration-none me-2"
                        style={{ color: 'inherit', cursor: 'pointer' }}
                      >
                        <i className="fa-solid fa-arrow-left"></i>
                      </Link>
                      <span className="text-truncate">{orderDetails.project?.name || orderDetails.project_name || orderDetails.name || "Order"}</span>
                    </h4>
                    {orderDetails.order?.identifier && (
                      <div className="d-flex align-items-center column-gap-2">
                        <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                          {orderDetails.order.identifier}
                        </span>
                        <i
                          onClick={downloadOrderDetails}
                          className="fa-solid fa-download fs-6 pointer text-secondary"
                          style={{ cursor: "pointer" }}
                          title="Download Package Summary"
                        />
                      </div>
                    )}
                  </div>
                  <div className="col-6 text-end">
                    {orderDetails.order?.price !== null && orderDetails.order?.price !== undefined && (
                      <div className="fw-bold mb-1" style={{ fontSize: '0.95rem' }}>
                        {formatCurrency(orderDetails.order.price)}
                      </div>
                    )}
                    {orderDetails.order.status && (
                      <div className="mb-1">
                        <span className={`badge ${
                          orderDetails.order.status === "delivered" ? "bg-success" :
                          orderDetails.order.status === "placed" ? "bg-primary" :
                          orderDetails.order.status === "production" ? "bg-info" :
                          "bg-secondary"
                        }`} style={{ fontSize: '0.75rem' }}>
                        {orderDetails.order.status === "production" || orderDetails.order.status === "production_planning" 
                            ? "In Production" 
                            : formatStatus(orderDetails.order.status)}                        </span>
                      </div>
                    )}
                    <Link
                      to={`/projects/${projectId}`}
                      className="text-decoration-none"
                      style={{ color: "#0d6efd", fontSize: '0.875rem' }}
                    >
                      View Project
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <style>{`
              .nav-tabs {
                border-bottom: 1px solid #dee2e6 !important;
              }
              .nav-tabs .nav-link {
                border: none !important;
                border-radius: 0 !important;
                background: transparent !important;
                margin-bottom: -1px !important;
              }
              .nav-tabs .nav-link.active {
                border-bottom: 2px solid #2C92E6 !important;
                border-top: none !important;
                border-left: none !important;
                border-right: none !important;
                background: transparent !important;
              }
              .nav-tabs .nav-link:hover {
                border-color: transparent !important;
                background: transparent !important;
                color: inherit !important;
              }
              .nav-tabs .nav-link:focus {
                border-color: transparent !important;
                background: transparent !important;
                box-shadow: none !important;
              }
            `}</style>
            {/* Tabs - Hide when shipment is selected */}
            <Nav tabs className="mb-3" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
          <NavItem style={{ flexShrink: 0 }}>
            <NavLink
              className={activeMainTab === "delivery" ? "active" : ""}
              onClick={() => {
                setActiveMainTab("delivery");
              }}
              style={{
                cursor: "pointer",
                border: 'none',
                borderBottom: activeMainTab === "delivery" ? '2px solid #2C92E6' : 'none',
                borderRadius: 0,
                background: 'transparent',
                padding: '0.5rem 1rem'
              }}
            >
              <svg width="28" height="20" viewBox="0 0 28 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: '100%', height: 'auto' }}>
                <path d="M21.25 18.125C22.2875 18.125 23.125 17.2875 23.125 16.25C23.125 15.2125 22.2875 14.375 21.25 14.375C20.2125 14.375 19.375 15.2125 19.375 16.25C19.375 17.2875 20.2125 18.125 21.25 18.125ZM23.125 6.875H20V10H25.575L23.125 6.875ZM6.25 18.125C7.2875 18.125 8.125 17.2875 8.125 16.25C8.125 15.2125 7.2875 14.375 6.25 14.375C5.2125 14.375 4.375 15.2125 4.375 16.25C4.375 17.2875 5.2125 18.125 6.25 18.125ZM23.75 5L27.5 10V16.25H25C25 18.325 23.325 20 21.25 20C19.175 20 17.5 18.325 17.5 16.25H10C10 18.325 8.325 20 6.25 20C4.175 20 2.5 18.325 2.5 16.25H0V2.5C0 1.1125 1.1125 0 2.5 0H20V5H23.75ZM2.5 2.5V13.75H3.45C4.1375 12.9875 5.1375 12.5 6.25 12.5C7.3625 12.5 8.3625 12.9875 9.05 13.75H17.5V2.5H2.5Z" fill={activeMainTab === "delivery" ? "#2C92E6" : "#666666"}/>
              </svg>
            </NavLink>
          </NavItem>
          <NavItem style={{ flexShrink: 0 }}>
            <NavLink
              className={activeMainTab === "payments" ? "active" : ""}
              onClick={() => {
                setActiveMainTab("payments");
                setSelectedShipment(null);
              }}
              style={{
                cursor: "pointer",
                border: 'none',
                borderBottom: activeMainTab === "payments" ? '2px solid #2C92E6' : 'none',
                borderRadius: 0,
                background: 'transparent',
                padding: '0.5rem 1rem'
              }}
            >
              <svg width="13" height="20" viewBox="0 0 13 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: '100%', height: 'auto' }}>
                <path d="M1.11111 0H12.2222L11.1111 2.22222H7.48889C8.02222 2.86667 8.42222 3.62222 8.65555 4.44444H12.2222L11.1111 6.66667H8.88889C8.75081 8.05181 8.14898 9.34972 7.18094 10.35C6.21291 11.3503 4.93543 11.9944 3.55556 12.1778V12.2222H2.77778L9.44444 20H6.66667L0 12.2222V10H2.77778C4.73333 10 6.35556 8.55556 6.62222 6.66667H0L1.11111 4.44444H6.28889C5.66667 3.13333 4.33333 2.22222 2.77778 2.22222H0L1.11111 0Z" fill={activeMainTab === "payments" ? "#2C92E6" : "#666666"}/>
              </svg>
            </NavLink>
            </NavItem>
          </Nav>
          </>
        )}

        {/* Tab Content */}
        <TabContent activeTab={activeMainTab} style={{ paddingLeft: 0, paddingRight: 0 }}>
          <TabPane tabId="delivery" style={{ paddingLeft: 0, paddingRight: 0 }}>
            {renderDeliveryTab()}
          </TabPane>
          <TabPane tabId="payments" style={{ paddingLeft: 0, paddingRight: 0 }}>
            {renderPaymentsTab()}
          </TabPane>
        </TabContent>
      </div>
    </>
  );
};

export default OrderDetails;
